import { useCallback } from "react";
import {
  callActionApiAsync,
  markAsReadTaskNotification
} from "../../services/apiService";
import MenuButton from "../../components/menuButton";
import { task_status_ENUM } from "../../constants/enums";

// 1 = Asignada a mí
// 2 = Yo asigné
// 3 = Completada
// 4 = Reasignada
// 5 = Justificada
// 6 = Eliminada
// 7 = Completada, requiere revisión de líder
const ORANGE_REVIEW_STYLES = {
  cardBg: "#FFFFFF",
  cardBorder: "#FED7AA",
  headerBg: "#EA580C",
  headerColor: "#FFFFFF",
  statusBg: "#EA580C",
  statusColor: "#FFFFFF"
};

function getNotificationStyles(type, taskState) {
  if (type === 7 || taskState === 6) return ORANGE_REVIEW_STYLES;
  switch (type) {
    case 3: // COMPLETADA
      return {
        cardBg: "#FFFFFF",
        cardBorder: "#D1FAE5",
        headerBg: "#22C55E",
        headerColor: "#FFFFFF",
        statusBg: "#22C55E",
        statusColor: "#FFFFFF"
      };

    case 4: // REASIGNADA
    case 5: // JUSTIFICADA
      return {
        cardBg: "#FFFFFF",
        cardBorder: "#FDE68A",
        headerBg: "#FACC15",
        headerColor: "#92400E",
        statusBg: "#4F46E5",
        statusColor: "#FFFFFF"
      };

    case 6: // ELIMINADA
      return {
        cardBg: "#FFF1F2",      // fondo rosado suave
        cardBorder: "#FECACA",  // borde rojo claro
        headerBg: "#EF4444",    // rojo fuerte
        headerColor: "#FFFFFF",
        statusBg: "#EF4444",
        statusColor: "#FFFFFF"
      };

    default: // 1, 2, otros -> asignación normal
      return {
        cardBg: "#FFFFFF",
        cardBorder: "#E5E7EB",
        headerBg: "#E0EAFF",
        headerColor: "#1D4ED8",
        statusBg: "#4F46E5",
        statusColor: "#FFFFFF"
      };
  }
}

function getStatusLabel(status) {
  if (status === task_status_ENUM.TO_DO) return "Por Hacer";
  if (status === task_status_ENUM.IN_PROCESS) return "En Proceso";
  if (status === task_status_ENUM.COMPLETED) return "Completada";
  if (status === task_status_ENUM.STATE_JUSTIFIED) return "Justificada";
  if (status === task_status_ENUM.DELETED) return "Eliminada";
  if (status === task_status_ENUM.COMPLETED_NEEDS_LEADER_REVIEW)
    return "Completada, requiere revisión de líder";
  return "Sin estado";
}

function TaskNotificationsMenu({
  currentUserId,
  notifications,
  setNotifications,
  onOpenTaskDetail
}) {
  const handleDismissNotification = useCallback(
    (e, n) => {
      e.stopPropagation();
      callActionApiAsync(
        () =>
          markAsReadTaskNotification({
            user_id: currentUserId,
            notification_id: n.id
          }),
        () => {
          setNotifications((prev) => prev.filter((x) => x.id !== n.id));
        }
      );
    },
    [currentUserId, setNotifications]
  );

  const handleClickCard = useCallback(
    (n) => {
      if (onOpenTaskDetail && n.task_id) {
        onOpenTaskDetail(n.task_id);
      }
    },
    [onOpenTaskDetail]
  );

  const badgeCount = notifications.length;

  return (
    <MenuButton
        title="Alertas"
        notification={badgeCount}
        dismissable={false}
      >
      <p
        className="not-title"
        style={{
          fontWeight: 600,
          marginBottom: 6,
          fontSize: 13
        }}
      >
        Organizador de Tareas
      </p>

      {notifications.length === 0 && (
        <p className="not-empty" style={{ fontSize: 12, opacity: 0.8 }}>
          No tienes alertas pendientes.
        </p>
      )}

      {/* Contenedor scrollable */}
      <div
        className="not-list scroll-thin"
        style={{
          maxHeight: 380,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 4,
          maxWidth: 300,
          boxSizing: "border-box"
        }}
      >
        {notifications.map((n) => {
          const styles = getNotificationStyles(n.type, n.task_state);
          const statusLabel = getStatusLabel(n.task_state);
          const canOpenDetail = onOpenTaskDetail && n.task_id;

          return (
            <div
              key={n.id}
              className="not-card"
              onClick={() => handleClickCard(n)}
              style={{
                cursor: canOpenDetail ? "pointer" : "default",
                padding: "8px 12px 10px",
                paddingTop: 10,
                marginBottom: 8,
                width: "100%",
                borderRadius: 14,
                border: `1px solid ${styles.cardBorder}`,
                backgroundColor: styles.cardBg,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                boxSizing: "border-box",
                position: "relative"
              }}
            >
              {/* Botón X para quitar de la lista (no se muestra en "Te asignaron una tarea") */}
              {n.type !== 1 && (
                <button
                  type="button"
                  aria-label="Cerrar notificación"
                  onClick={(e) => handleDismissNotification(e, n)}
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,0.08)",
                    color: "#374151",
                    fontSize: 14,
                    lineHeight: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0
                  }}
                >
                  ×
                </button>
              )}

              {/* Pill de tipo de notificación */}
              <div
                style={{
                  alignSelf: "flex-start",
                  padding: "4px 9px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  backgroundColor: styles.headerBg,
                  color: styles.headerColor,
                  marginBottom: 2
                }}
              >
                {n.type_label}
              </div>

              {/* Descripción de la tarea */}
              <div
                style={{
                  fontSize: 12,
                  marginBottom: 1,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <span style={{ fontWeight: 600, marginRight: 4 }}>Tarea:</span>
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  title={n.task_description}
                >
                  {n.task_description}
                </span>
              </div>

              <div style={{ fontSize: 11.5 }}>
                <span style={{ fontWeight: 600 }}>Creador: </span>
                <span>{n.creator_name}</span>
              </div>

              {n.last_assignee_name && (
                <div style={{ fontSize: 11.5 }}>
                  <span style={{ fontWeight: 600 }}>Último asignado: </span>
                  <span>{n.last_assignee_name}</span>
                </div>
              )}

              {/* Estado de la tarea */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 2
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "#4B5563",
                    fontWeight: 500,
                    marginRight: 6
                  }}
                >
                  Estado:
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: "4px 9px",
                    borderRadius: 999,
                    backgroundColor: styles.statusBg,
                    color: styles.statusColor
                  }}
                >
                  {statusLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </MenuButton>
  );
}

export default TaskNotificationsMenu;
